// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

const ellipsisProps: TypographyPresentationProps = {
    weight: "bold",
    css: css`
        position: relative;
        visibility: hidden;
        display: inline-block;
        text-align: left;
        letter-spacing: inherit;
        margin-bottom: 0;
        white-space: nowrap;
        &::after {
            visibility: visible;
            position: absolute;
            left: 0;
            overflow: hidden;
            display: inline-block;
            vertical-align: bottom;
            animation: ellipsis steps(4, end) 500ms infinite 0ms;
            content: "...";
            width: 0;
        }

        @keyframes ellipsis {
            to {
                width: 1.25em;
            }
        }
    `,
};

interface Props extends ButtonProps {
    showSpinner: boolean | undefined;
    label: string | undefined;
}

type EllipsisSpinnerProps = Props & ThemeProps<BaseTheme>;

const EllipsisSpinner: React.FC<EllipsisSpinnerProps> = ({ label, showSpinner, id, ...otherProps }) => {
    const { spreadProps } = applyPropBuilder(
        { ...otherProps },
        {
            component: "button",
            propKey: "primary",
        },
    );

    return (
        <>
            {label ? <Typography {...spreadProps("typographyProps")} id={id}>{label}</Typography> : null}
            {showSpinner ? (
                <Typography {...spreadProps("typographyProps")} {...ellipsisProps} aria-hidden="true" id={`${id}_ellipsis`}>
                    ...
                </Typography>
            ) : null}
        </>
    );
};

export default withTheme(EllipsisSpinner);
